import { AuthProvider, PrivateRoute } from "@app/providers/auth";
import { lazy } from "react";
import { Navigate } from "react-router-dom";

const DashboardLayout = lazy(
  () => import("@pages/dashboard/ui/DashboardLayout"),
);
const DashboardHome = lazy(() => import("@pages/dashboard/Home/DashboardHome"));
const OnboardingProvider = lazy(() => import("@app/providers/onboarding/OnboardingProvider"));

const BotTeamsLayout = lazy(() => import("@pages/bot-teams/ui/BotTeamsLayout"));
const TeamsListPage = lazy(
  () => import("@pages/bot-teams/ui/TeamsListPage/TeamsListPage"),
);
const BotTeamsInstructionPage = lazy(
  () => import("@pages/bot-teams/ui/BotTeamsInstructionPage/BotTeamsInstructionPage"),
);
const WorkflowPage = lazy(() => import("@pages/workflow/ui/WorkflowPage"));

const BotCreatePage = lazy(() => import("@pages/bot-create/ui/BotCreatePage"));
const BotCreateManualPage = lazy(
  () => import("@pages/bot-create/ui/BotCreateManualPage"),
);
const BotCreateSmartPage = lazy(
  () => import("@pages/bot-create/ui/BotCreateSmartPage"),
);

const BusinessCardsLayout = lazy(
  () => import("@pages/business-cards/ui/BusinessCardsLayout"),
);
const BusinessCardsPage = lazy(
  () => import("@pages/business-cards/ui/BusinessCardsPage"),
);
const BusinessCardCreatePage = lazy(
  () =>
    import("@pages/business-cards/ui/BusinessCardCreatePage/BusinessCardCreatePage"),
);
const BusinessCardEditPage = lazy(
  () =>
    import("@pages/business-cards/ui/BusinessCardEditPage/BusinessCardEditPage"),
);
const BusinessCardCrmPage = lazy(
  () =>
    import("@pages/business-cards/ui/BusinessCardCrmPage/BusinessCardCrmPage"),
);
const BusinessCardCoursesPage = lazy(
  () =>
    import("@pages/business-cards/ui/BusinessCardCoursesPage/BusinessCardCoursesPage"),
);
const BusinessCardCoursePage = lazy(
  () =>
    import("@pages/business-cards/ui/BusinessCardCoursePage/BusinessCardCoursePage"),
);
const BusinessCardStatsPage = lazy(
  () =>
    import("@pages/business-cards/ui/BusinessCardStatsPage/BusinessCardStatsPage"),
);
const ProfileCardPage = lazy(
  () => import("@pages/profile-card/ui/ProfileCardPage"),
);
const BusinessCardPage = lazy(
  () => import("@pages/profile-card/ui/BusinessCardPage"),
);

const TelegramCreatePage = lazy(
  () => import("@pages/channel-create/ui/TelegramCreatePage"),
);
const WhatsappCreatePage = lazy(
  () => import("@pages/channel-create/ui/WhatsappCreatePage"),
);
const InstagramCreatePage = lazy(
  () => import("@pages/channel-create/ui/InstagramCreatePage"),
);
const WidgetCreatePage = lazy(
  () => import("@pages/channel-create/ui/WidgetCreatePage"),
);
const ChannelEditPage = lazy(
  () => import("@pages/channel-edit/ui/ChannelEditPage"),
);
const WhatsappEditPage = lazy(
  () => import("@pages/channel-edit/ui/WhatsappEditPage"),
);
const WidgetEditPage = lazy(
  () => import("@pages/channel-edit/ui/WidgetEditPage"),
);
const InstagramEditPage = lazy(
  () => import("@pages/channel-edit/ui/InstagramEditPage"),
);

const ProfileLayout = lazy(() => import("@pages/profile/ui/ProfileLayout"));
const DatabaseCreatePage = lazy(
  () => import("@pages/database-create/ui/DatabaseCreatePage"),
);
const RewardsLayout = lazy(() => import("@pages/rewards/ui/RewardsLayout"));
const RewardsListPage = lazy(
  () => import("@pages/rewards/ui/RewardsListPage/RewardsListPage"),
);

const MessagesPage = lazy(
  () => import("@pages/account/pages/messages/MessagesPage"),
);
const ChannelsPage = lazy(
  () => import("@pages/account/pages/channels/ui/ChannelsPage"),
);
const DatabasePage = lazy(
  () => import("@pages/account/pages/database/ui/DatabasePage"),
);
const TeamPage = lazy(
  () => import("@pages/account/pages/team/ui/TeamPage"),
);
const PaymentsPage = lazy(
  () => import("@pages/account/pages/payments/ui/PaymentsPage"),
);

const BotEditLayout = lazy(() => import("@pages/bot-edit/ui/BotEditLayout"));
const DatabaseEdit = lazy(() => import("@pages/database-edit/ui/DatabaseEdit"));
const BotTemplatePage = lazy(
  () => import("@pages/bot-template/ui/BotTemplatePage"),
);
const IntegrationsPage = lazy(
  () => import("@pages/integrations/IntegrationsPage"),
);
const RewardStats = lazy(
  () => import("@pages/rewards/ui/RewardStatsPage/RewardStats"),
);
const Education = lazy(() => import("@pages/education/Education"));
const MyBots = lazy(() => import("@pages/dashboard/MyBots/MyBots"));
const AiGallery = lazy(() => import("@pages/ai-gallery/ui/AiGallery"));
const News = lazy(() => import("@pages/news/ui/NewsPage"));
const NewsItem = lazy(() => import("@pages/news-item/ui/NewsItemPage"));
const FAQ = lazy(() => import("@pages/faq/ui/FAQPage"));
const ProfileMainDataPage = lazy(() => import("@pages/profile/ui/ProfileMainDataPage"));
const ProfileContactsPage = lazy(() => import("@pages/profile/ui/ProfileContactsPage"));
const ProfileLocationPage = lazy(() => import("@pages/profile/ui/ProfileLocationPage"));
const ProfileSecurityPage = lazy(() => import("@pages/profile/ui/ProfileSecurityPage"));
const ProfileSupportPage = lazy(() => import("@pages/profile/ui/ProfileSupportPage"));
const ProfileSupportTicketPage = lazy(() => import("@pages/profile/ui/ProfileSupportTicketPage"));

const MainSettingsWrapper = lazy(
  () =>
    import("@pages/bot-edit/components/main-settings/ui/MainSettingsWrapper"),
);
const ScheduleWrapper = lazy(
  () => import("@pages/bot-edit/components/schedule/ui/ScheduleWrapper"),
);
const BotTestChatPage = lazy(
  () => import("@pages/bot-edit/components/test-chat/ui/BotTestChatPage"),
);
const IntegrationWrapper = lazy(
  () => import("@pages/bot-edit/components/integrations/ui/IntegrationWrapper"),
);
const AdvancedSettingsWrapper = lazy(
  () =>
    import(
      "@pages/bot-edit/components/advanced-settings/ui/AdvancedSettingsWrapper"
    ),
);
const ResponseParams = lazy(
  () =>
    import(
      "@pages/bot-edit/components/advanced-settings/ui/sections/ResponseParams"
    ),
);
const FollowUps = lazy(
  () =>
    import(
      "@pages/bot-edit/components/advanced-settings/ui/sections/FollowUps"
    ),
);
const KnowledgeBaseSettings = lazy(
  () =>
    import(
      "@pages/bot-edit/components/advanced-settings/ui/sections/KnowledgeBaseSettings"
    ),
);
const CustomVariables = lazy(
  () =>
    import(
      "@pages/bot-edit/components/advanced-settings/ui/sections/CustomVariables"
    ),
);
const StandardFunctions = lazy(
  () =>
    import(
      "@pages/bot-edit/components/advanced-settings/ui/sections/StandardFunctions"
    ),
);
const SecuritySettings = lazy(
  () =>
    import(
      "@pages/bot-edit/components/advanced-settings/ui/sections/SecuritySettings"
    ),
);
const SpamProtection = lazy(
  () =>
    import(
      "@pages/bot-edit/components/advanced-settings/ui/sections/SpamProtection"
    ),
);
const NotFound = lazy(() => import("@pages/not-found/NotFound"));

export const mainRoutes = {
  path: "/main/:response?",
  element: (
    <AuthProvider>
      <PrivateRoute>
        <OnboardingProvider>
          <DashboardLayout />
        </OnboardingProvider>
      </PrivateRoute>
    </AuthProvider>
  ),
  children: [
    { index: true, element: <DashboardHome /> },
    { path: "dialogs/:id?", element: <MessagesPage /> },
    { path: "channels", element: <ChannelsPage /> },
    { path: "knowledge-bases", element: <DatabasePage /> },
    {
      path: "profile",
      element: <ProfileLayout />,
      children: [
        { path: "main", element: <ProfileMainDataPage /> },
        { path: "contacts", element: <ProfileContactsPage /> },
        { path: "location", element: <ProfileLocationPage /> },
        { path: "security", element: <ProfileSecurityPage /> },
        { path: "team", element: <TeamPage /> },
        { path: "payments", element: <PaymentsPage /> },
        { path: "support", element: <ProfileSupportPage /> },
        { path: "support/:id", element: <ProfileSupportTicketPage /> },
        { path: "integrations", element: <IntegrationsPage /> },
      ],
    },
    {
      path: "bot-teams",
      element: <BotTeamsLayout />,
      children: [
        { index: true, element: <TeamsListPage /> },
        { path: "instruction", element: <BotTeamsInstructionPage /> },
        { path: "workflow/:id?", element: <WorkflowPage /> },
      ],
    },
    {
      path: "bot-create",
      children: [
        { index: true, element: <BotCreatePage /> },
        { path: "manual", element: <BotCreateManualPage /> },
        { path: "smart", element: <BotCreateSmartPage /> },
      ],
    },
    {
      path: "business-cards",
      element: <BusinessCardsLayout />,
      children: [
        { index: true, element: <BusinessCardsPage /> },
        { path: ":id?", element: <ProfileCardPage /> },
        { path: "card/:id?", element: <BusinessCardPage /> },
        { path: "create", element: <BusinessCardCreatePage /> },
        { path: "edit/:id?", element: <BusinessCardEditPage /> },
        { path: "crm", element: <BusinessCardCrmPage /> },
        { path: "courses", element: <BusinessCardCoursesPage /> },
        { path: "course/:id", element: <BusinessCardCoursePage /> },
        { path: "statistics/:id?", element: <BusinessCardStatsPage /> },
      ],
    },
    { path: "database-create", element: <DatabaseCreatePage /> },
    { path: "telegram-create", element: <TelegramCreatePage /> },
    { path: "whatsapp-create", element: <WhatsappCreatePage /> },
    { path: "instagram-create", element: <InstagramCreatePage /> },
    { path: "widget-create", element: <WidgetCreatePage /> },
    { path: "channel-edit/:id", element: <ChannelEditPage /> },
    { path: "whatsapp-edit/:id", element: <WhatsappEditPage /> },
    { path: "widget-edit/:id", element: <WidgetEditPage /> },
    { path: "instagram-edit/:id", element: <InstagramEditPage /> },
    { path: "faq", element: <FAQ /> },
    {
      path: "rewards",
      element: <RewardsLayout />,
      children: [
        { index: true, element: <RewardsListPage /> },
        { path: "statistics", element: <RewardStats /> },
      ],
    },
    { path: "education", element: <Education /> },
    {
      path: "bot/:id",
      element: <BotEditLayout />,
      children: [
        { path: "settings", element: <MainSettingsWrapper /> },
        { path: "working-hours", element: <ScheduleWrapper /> },
        { path: "test", element: <BotTestChatPage /> },
        { path: "integrations", element: <IntegrationWrapper /> },
        {
          path: "advanced-settings",
          element: <AdvancedSettingsWrapper />,
          children: [
            { index: true, element: <Navigate to="response-params" replace /> },
            { path: "response-params", element: <ResponseParams /> },
            { path: "follow-ups", element: <FollowUps /> },
            { path: "knowledge-base", element: <KnowledgeBaseSettings /> },
            { path: "custom-variables", element: <CustomVariables /> },
            { path: "standard-functions", element: <StandardFunctions /> },
            { path: "security", element: <SecuritySettings /> },
            { path: "spam-protection", element: <SpamProtection /> },
          ],
        },
      ],
    },
    { path: "bot-template/:id?", element: <BotTemplatePage /> },
    { path: "database/:id", element: <DatabaseEdit /> },
    {
      path: "news",
      children: [
        { index: true, element: <News /> },
        { path: ":id", element: <NewsItem /> },
      ],
    },
    { path: "assistants-gallery", element: <AiGallery /> },
    { path: "my-bots", element: <MyBots /> },
    { path: "*", element: <NotFound /> },
  ],
};
